// src/pages/TestsPage.tsx
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Typography, Divider, Skeleton, Stack, Box, Container, Chip } from '@mui/material';
import { supabase, markingDb } from '../supabaseClient.ts';
import { useEffectiveStudentId } from '../hooks/useEffectiveStudentId.ts';

type Test = {
  id: string;
  name: string;
  description: string | null;
  assigned_at: string;
  attempts: number;
  max_attempts: number | null;
  best_pct: number | null; // best score as percentage (0–100)
  latest_result_id?: string | null; // newest attempt id (by finished_at)
  latest_pending_manual?: boolean;
};

const fmt = (iso: string) =>
  new Date(iso).toLocaleString('en-AU', {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: 'Australia/Sydney',
  });

const rowButtonSx = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 2,
  width: '100%',
  p: 2,
  textTransform: 'none',
  color: '#333',
  border: '1px solid #b3b3b3',
  borderRadius: 8,
  boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
  transition: 'transform 80ms ease',
  '&:hover': { transform: 'translateY(-1px)' },
  '&:active': { transform: 'translateY(1px)' },
  '&.Mui-disabled': { opacity: 1, color: '#333' },
} as const;

const TestsPage: React.FC = () => {
  const { studentId, loading: studentIdLoading } = useEffectiveStudentId();
  const [current, setCurrent] = useState<Test[]>([]);
  const [completed, setCompleted] = useState<Test[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (studentIdLoading) return;
    (async () => {
      setLoading(true);
      if (!studentId) {
        setError('Student record not found.');
        setLoading(false);
        return;
      }

      // 1) Fetch assignments + test metadata
      const { data: assignments, error: asgErr } = await supabase
        .from('test_assignments')
        .select(
          'assigned_at, max_attempts, tests!inner(id, name, description, max_attempts, deleted_at)'
        )
        .eq('student_id', studentId)
        .is('tests.deleted_at', null)
        .order('assigned_at', { ascending: false });

      if (asgErr) {
        setError(asgErr.message);
        setLoading(false);
        return;
      }

      const baseTests: Test[] = (assignments ?? [])
        .filter((row: any) => row.tests)
        .map((row: any) => {
          const t = row.tests;
          return {
            id: t.id,
            name: t.name,
            description: t.description,
            assigned_at: row.assigned_at,
            attempts: 0,
            max_attempts: row.max_attempts ?? t.max_attempts ?? null,
            best_pct: null,
            latest_result_id: null,
          };
        });

      // Nothing assigned at all
      if (baseTests.length === 0) {
        setCurrent([]);
        setCompleted([]);
        setLoading(false);
        return;
      }

      // 2) Filter out tests that have zero questions
      const allIds = baseTests.map((t) => t.id);
      const { data: tqRows, error: tqErr } = await supabase
        .from('test_questions')
        .select('test_id')
        .in('test_id', allIds);
      if (tqErr) {
        setError(tqErr.message);
        setLoading(false);
        return;
      }
      const nonEmptySet = new Set((tqRows ?? []).map((r) => r.test_id as string));
      const nonEmptyTests = baseTests.filter((t) => nonEmptySet.has(t.id));

      if (nonEmptyTests.length === 0) {
        setCurrent([]);
        setCompleted([]);
        setLoading(false);
        return;
      }

      // 3) Pull results for remaining tests (use finished_at instead of created_at)
      const testIds = nonEmptyTests.map((t) => t.id);
      let results: any[] | null = null;
      let resErr: any = null;
      const withMarking = await markingDb
        .from('test_results')
        .select('id, test_id, score, total_marks, finished_at, marking_status')
        .eq('student_id', studentId)
        .in('test_id', testIds);
      results = withMarking.data;
      resErr = withMarking.error;
      if (
        resErr &&
        typeof resErr.message === 'string' &&
        (resErr.message.includes('marking_status') || resErr.message.includes('schema cache'))
      ) {
        const legacy = await markingDb
          .from('test_results')
          .select('id, test_id, score, total_marks, finished_at')
          .eq('student_id', studentId)
          .in('test_id', testIds);
        results = legacy.data;
        resErr = legacy.error;
      }

      if (resErr) {
        setError(resErr.message);
        setLoading(false);
        return;
      }

      const byTest: Record<
        string,
        {
          count: number;
          bestPct: number | null;
          latestId: string | null;
          latestTs: string | null;
          latestPendingManual: boolean;
        }
      > = {};

      for (const r of results ?? []) {
        const isFinal = !('marking_status' in (r as any)) || (r as any).marking_status !== 'pending_manual';
        const pct =
          isFinal && r.total_marks
            ? (Number(r.score) / Number(r.total_marks)) * 100
            : null;
        const cur =
          byTest[r.test_id] ?? {
            count: 0,
            bestPct: null,
            latestId: null,
            latestTs: null,
            latestPendingManual: false,
          };

        cur.count += 1;
        if (pct != null) cur.bestPct = Math.max(cur.bestPct ?? 0, pct);

        const ts = (r.finished_at as string) || '';
        if (ts && (!cur.latestTs || ts > cur.latestTs)) {
          cur.latestTs = ts;
          cur.latestId = String(r.id);
          cur.latestPendingManual = !isFinal;
        }
        byTest[r.test_id] = cur;
      }

      const withStats: Test[] = nonEmptyTests.map((t) => ({
        ...t,
        attempts: byTest[t.id]?.count ?? 0,
        best_pct: byTest[t.id]?.bestPct != null ? Math.round(byTest[t.id]!.bestPct!) : null,
        latest_result_id: byTest[t.id]?.latestId ?? null,
        latest_pending_manual: byTest[t.id]?.latestPendingManual ?? false,
      }));

      const currentTests = withStats.filter(
        (t) => t.max_attempts == null || t.attempts < t.max_attempts
      );
      const completedTests = withStats.filter(
        (t) => t.max_attempts != null && t.attempts >= t.max_attempts
      );

      setCurrent(currentTests);
      setCompleted(completedTests);
      setLoading(false);
    })();
  }, [studentId, studentIdLoading]);

  const AttemptsCell = ({ t }: { t: Test }) => (
    <Typography
      variant="body2"
      sx={{ paddingRight: '2rem', minWidth: '140px', textAlign: 'right' }}
    >
      {t.max_attempts == null
        ? `${t.attempts} ${t.attempts === 1 ? 'attempt' : 'attempts'}`
        : `${t.attempts} / ${t.max_attempts} attempts`}
    </Typography>
  );

  const BestCell = ({ t }: { t: Test }) => (
    <Typography
      variant="body2"
      sx={{ paddingRight: '2rem', minWidth: '140px', textAlign: 'right' }}
    >
      {t.best_pct != null ? `Best: ${t.best_pct}%` : 'Best: —'}
    </Typography>
  );

  const Section = ({
    title,
    items,
    empty,
    linkFor,
    rightCell,
  }: {
    title: string;
    items: Test[];
    empty: string;
    linkFor: (t: Test) => string | null;
    rightCell: (t: Test) => React.ReactNode;
  }) => (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h5" component="h2" sx={{ mb: 2 }}>
        {title}
      </Typography>
      {items.length === 0 ? (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {empty}
        </Typography>
      ) : (
        <Stack spacing={2} sx={{ mt: 1 }}>
          {items.map((t) => {
            const to = linkFor(t);
            const clickable = Boolean(to);
            return (
              <Box key={t.id} sx={{ pb: 1.875 }}>
                <Button
                  sx={rowButtonSx}
                  component={clickable ? Link : (undefined as any)}
                  to={to ?? undefined}
                  disabled={!clickable}
                  disableRipple={!clickable}
                >
                  <Box
                    sx={{
                      pl: 4,
                      display: 'flex',
                      flexDirection: 'row',
                      gap: 3,
                      alignItems: 'center',
                      flexGrow: 1,
                    }}
                  >
                    <Typography variant="subtitle1">
                      {t.name}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {fmt(t.assigned_at)}
                    </Typography>
                    {t.latest_pending_manual && (
                      <Chip
                        size="small"
                        label="Pending marking"
                        color="warning"
                        variant="filled"
                      />
                    )}
                  </Box>
                  {rightCell(t)}
                </Button>
              </Box>
            );
          })}
        </Stack>
      )}
    </Box>
  );

  /* ------- Skeleton while loading ------- */
  if (loading) {
    const RowSkeleton = () => (
      <Skeleton variant="rounded" animation="wave" height={56} sx={{ borderRadius: 1, mb: 1.5 }} />
    );
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Stack spacing={2}>
          <Skeleton variant="text" width={180} height={36} />
          <Box>
            <RowSkeleton />
            <RowSkeleton />
            <RowSkeleton />
          </Box>

          <Divider sx={{ my: 1 }} />

          <Skeleton variant="text" width={210} height={36} />
          <Box>
            <RowSkeleton />
            <RowSkeleton />
          </Box>
        </Stack>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Typography color="error">{error}</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Section
        title="Current Tests"
        items={current}
        empty="No current tests! 🎉"
        linkFor={(t) => `/tests/${t.id}`} // take test
        rightCell={(t) => <AttemptsCell t={t} />}
      />

      <Divider sx={{ my: 2 }} />

      <Section
        title="Completed Tests"
        items={completed}
        empty="You haven't completed any tests yet."
        // open latest attempt directly in the test page's review mode
        linkFor={(t) => (t.latest_result_id ? `/tests/${t.id}/result/${t.latest_result_id}` : null)}
        rightCell={(t) => <BestCell t={t} />}
      />
    </Container>
  );
};

export default TestsPage;
